import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { programMembershipSelectors } from '../../models/program-membership';
import useContentFetcher from '../../common/use-content-fetcher';
import { Spinner } from '../../components/ui';
import ContentDetail from './content-detail';
import ContentInfo from './content-info';
import { trackContentCardRead } from '../../models/content/analytics';
import './content-details.scss';
import { setStorageItem } from '../../lib/localstorage';
import ContentDetailsNotFound from '../email-content-details/content-details-not-found';
import { ContentDetail as ContentDetailV2 } from '../../components/v2/content-detail/content-detail';
import { Feature } from '../../models/features/features';
import { useHistory, useRouteMatch } from 'react-router';
import useFeatureFlag from '../../common/use-feature-flag';
import { useContentHeaderMinimizer } from '../../common/use-content-header-minimizer';
import { useArticleCloseTracking } from '../../hooks/useArticleCloseTracking';

const ContentDetailsOrBlank = (props) =>
  props.isActiveMembership ? <ContentDetails {...props} /> : null;

const ContentDetails = ({ contentId, feedItemId, isUgcPreview }) => {
  const { path } = useRouteMatch();
  const history = useHistory();

  const isApproval = path.includes('approvals');
  const { content, fetching } = useContentFetcher({
    contentId,
    feedItemId,
    isApproval,
    isUgcPreview,
    mentionFormat: 'with_metadata',
  });

  contentId = content && content.id;

  const newContentPage = useFeatureFlag(Feature.CONTENT_DETAIL_NEW);

  const loadComplete = !fetching;

  // FE-33336: when the article body iframe (server-rendered via patron's
  // EmbedController.showArticle) hits a 401/403/404 from mojo — e.g. a
  // Focused Content post the viewer isn't in the audience of — it posts a
  // message to this window. We swap the wrapper for ContentDetailsNotFound
  // so the whole page becomes the hot-air balloon, matching the refresh
  // path's UX. Same iframe → SPA bridge used by embed-video.ejs →
  // VideoMonitor.
  const [iframeUnavailable, setIframeUnavailable] = useState(false);
  useEffect(() => {
    // Reset on navigation so a previously-blocked content doesn't keep the
    // wrapper in NotFound when the user navigates to a different, accessible
    // post without an unmount.
    setIframeUnavailable(false);
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (
        event.data?.event === 'content-not-accessible' &&
        event.data?.source === 'patron-embed-article' &&
        Number(event.data.contentId) === Number(contentId)
      ) {
        setIframeUnavailable(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [contentId]);

  const showNotFound = (loadComplete && !content) || iframeUnavailable;

  const className = cx('content-details', {
    'content-details-v2': newContentPage,
  });

  useContentHeaderMinimizer({ loadSuccess: loadComplete && content });

  useArticleCloseTracking({
    contentId,
    enabled:
      loadComplete &&
      !!content &&
      content.content_type === 'article' &&
      !!newContentPage,
    wordCount: content?.word_count,
  });

  const navigateBack = () => {
    history.goBack();
  };

  useEffect(() => {
    setStorageItem('prevPage', 'article');
  }, []);

  useEffect(() => {
    if (feedItemId && content) {
      history.replace(`/contents/${content.id}`);
    } else if (!fetching && content) {
      trackContentCardRead(contentId);
    }
  }, [contentId, fetching]); // eslint-disable-line react-hooks/exhaustive-deps

  if (showNotFound) return <ContentDetailsNotFound />;

  return (
    <section className={className}>
      {fetching ? (
        <Spinner />
      ) : newContentPage ? (
        <ContentDetailV2
          contentId={contentId}
          navigateBack={navigateBack}
          isUgcPreview={isUgcPreview}
        />
      ) : (
        <>
          <ContentDetail contentId={contentId} onClose={navigateBack} />
          <ContentInfo contentId={contentId} />
        </>
      )}
    </section>
  );
};

const mapStateToProps = (state, ownProps) => ({
  isActiveMembership: programMembershipSelectors.getProgramMembershipIsActive(
    state,
    ownProps
  ),
  overlays: state.ui.overlays,
});

export default connect(mapStateToProps, null)(ContentDetailsOrBlank);
